import React, { useCallback, useContext, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { ChevronLeftOutlined, ChevronRightOutlined } from '@material-ui/icons';
import PropTypes from 'prop-types';

import { IconTooltip, IconButton } from 'components';
import {
  BookLocationDocumentValuesContext,
  UserAgentValuesContext,
  UserContentItemValuesContext,
} from 'contexts';
import { useKeyboardShortcut } from 'hooks';
import { NAVIGATION_BUTTONS } from 'constants';

const useStyles = makeStyles((theme) => ({
  nextButton: {
    backgroundColor: ({ shouldShowNextTooltip }) =>
      shouldShowNextTooltip ? theme.palette.shelfBlue['50L'] : 'transparent',
  },
}));

const NextAndBackButtons = ({
  canNavigate,
  nextLocation: nextLocationProp, // from ReaderSlider: next doc by displayed position (for offline)
  onChooseNewLocation,
  previousLocation: previousLocationProp, // from ReaderSlider: previous doc by displayed position (for offline)
  setAriaMsgForSliderTooltip,
  setValueLabelDisplay,
}) => {
  const { nextDocumentLocation, previousDocumentLocation, isEndOfDocument } =
    useContext(BookLocationDocumentValuesContext);
  const { userAgent } = useContext(UserAgentValuesContext);
  const { openDialogs } = useContext(UserContentItemValuesContext);

  // Props = next/prev from displayed position (ReaderSlider);
  // so offline each press advances. Fallback to context (from currentBookLocation).
  const nextLocation = nextLocationProp ?? nextDocumentLocation;
  const previousLocation = previousLocationProp ?? previousDocumentLocation;

  const [isNextTooltipOpen, setIsNextTooltipOpen] = useState(false);
  const [isForwardTooltipOpen, setIsForwardTooltipOpen] = useState(false);
  const [canShowNextTooltip, setCanShowNextTooltip] = useState(false);

  // if we navigate away from the end of the document
  // or to a different document, allow the tooltip again
  useEffect(() => {
    setCanShowNextTooltip(true);
  }, [isEndOfDocument, nextLocation]);

  const shouldShowNextTooltip =
    isNextTooltipOpen ||
    (isEndOfDocument &&
      canNavigate &&
      nextLocation &&
      !isForwardTooltipOpen &&
      canShowNextTooltip);

  const classes = useStyles({
    shouldShowNextTooltip,
  });

  const valueLabelAuto = useCallback(() => {
    setValueLabelDisplay('auto');
  }, [setValueLabelDisplay]);

  const updateSliderDisplay = useCallback(
    (newLocation) => {
      setValueLabelDisplay('on');
      const pageLabel =
        newLocation?.page?.label ?? newLocation?.document?.pages?.[0]?.label;
      const markerLabel =
        newLocation?.page?.marker?.label ??
        newLocation?.document?.pages?.[0]?.marker?.label ??
        '';
      setAriaMsgForSliderTooltip(
        `Page ${pageLabel ?? ''} ${markerLabel}`.trim(),
      );
    },
    [setValueLabelDisplay, setAriaMsgForSliderTooltip],
  );

  const goForward = useCallback(async () => {
    if (
      document.activeElement.tagName === 'TEXTAREA' ||
      document.activeElement.tagName === 'INPUT' ||
      Boolean(openDialogs.length)
    ) {
      return;
    }

    if (canNavigate && nextLocation) {
      // Firefox needs this to announce tooltip on VO for arrow navigation correctly
      setAriaMsgForSliderTooltip(null);

      await onChooseNewLocation(nextLocation, () =>
        updateSliderDisplay(nextLocation),
      );
    }
  }, [
    canNavigate,
    nextLocation,
    onChooseNewLocation,
    openDialogs.length,
    setAriaMsgForSliderTooltip,
    updateSliderDisplay,
  ]);

  const goBackward = useCallback(async () => {
    if (
      document.activeElement.tagName === 'TEXTAREA' ||
      document.activeElement.tagName === 'INPUT' ||
      Boolean(openDialogs.length)
    ) {
      return;
    }

    if (canNavigate && previousLocation) {
      // Firefox needs this to announce tooltip on VO for arrow navigation correctly
      setAriaMsgForSliderTooltip(null);

      await onChooseNewLocation(previousLocation, () =>
        updateSliderDisplay(previousLocation),
      );
    }
  }, [
    canNavigate,
    onChooseNewLocation,
    openDialogs.length,
    previousLocation,
    setAriaMsgForSliderTooltip,
    updateSliderDisplay,
  ]);

  const optAltKey = userAgent.isMac ? 'option' : 'alt';

  useKeyboardShortcut(`shift+${optAltKey}+n`, goBackward, {
    event: 'keydown',
  });
  useKeyboardShortcut(`${optAltKey}+n`, goForward, {
    event: 'keydown',
  });

  return (
    <>
      <IconTooltip
        id="prev-page"
        disabled={!canNavigate || !previousLocation}
        title={NAVIGATION_BUTTONS.PREVIOUS}
        open={isForwardTooltipOpen}
        onOpen={() => {
          setIsForwardTooltipOpen(true);
          setIsNextTooltipOpen(false);
          setCanShowNextTooltip(false);
        }}
        onClose={() => {
          setIsForwardTooltipOpen(false);
        }}
      >
        <IconButton
          aria-label={NAVIGATION_BUTTONS.PREVIOUS}
          disabled={!canNavigate || !previousLocation}
          onBlur={valueLabelAuto}
          onClick={goBackward}
          onMouseLeave={valueLabelAuto}
        >
          <ChevronLeftOutlined />
        </IconButton>
      </IconTooltip>
      <IconTooltip
        id="next-page"
        title={NAVIGATION_BUTTONS.NEXT}
        open={shouldShowNextTooltip}
        onClose={() => setIsNextTooltipOpen(false)}
        onOpen={() => setIsNextTooltipOpen(true)}
        disabled={!canNavigate || !nextLocation}
      >
        <IconButton
          className={classes.nextButton}
          aria-label={NAVIGATION_BUTTONS.NEXT}
          disabled={!canNavigate || !nextLocation}
          onBlur={valueLabelAuto}
          onClick={goForward}
          onMouseLeave={valueLabelAuto}
        >
          <ChevronRightOutlined />
        </IconButton>
      </IconTooltip>
    </>
  );
};

NextAndBackButtons.propTypes = {
  canNavigate: PropTypes.bool,
  nextLocation: PropTypes.object,
  onChooseNewLocation: PropTypes.func,
  previousLocation: PropTypes.object,
  setAriaMsgForSliderTooltip: PropTypes.func,
  setValueLabelDisplay: PropTypes.func,
};

export default NextAndBackButtons;
